// MACHINERY V2 (Stroje) — the katalog filter sheet's VYROBCA section (M2.16c).
//
// The below-1280 counterpart of CatalogBrandFacets.tsx, which is the rail's
// rendering and is built on 13px rail rows. Same DATA — `buildCatalogFacets` and
// `visibleFacetRows` are pure and are reused untouched, so a brand row can never
// group or cap differently on a phone than on a desktop — and different ROWS:
// 44px `SheetCheckRow`s from the mobile kit.
//
// >>> NO SECOND EXPANDER STATE MACHINE. <<< The rail draws a chevron inside each
// parent row that expands the family WITHOUT selecting it, which needs a
// `Set<string>` of expanded parents and a nested control that swallows both
// activation paths. The sheet uses the idiom fleet/fleetSheetSections.tsx
// already uses for TYP STROJA -> vyrobca instead: children appear indented under
// their parent while that family is the SELECTED one. Two taps to reach a child
// rather than one, and in exchange there is no second, smaller control inside a
// 44px row — which on a phone is the trade the touch law is asking for. §11's
// "Parent filters all children" is what makes the intermediate state useful
// rather than a dead step.
//
// ============================================================
//  >>> "THE SELECTED FAMILY", NOT "THE SELECTED PARENT" — review finding F1
// ============================================================
// The fleet idiom this copied works because the fleet stages TWO INDEPENDENT
// draft fields: `draft.typeId` keeps the parent row selected while
// `draft.manufacturerId` selects the child (fleetSheetSections.tsx:175-186).
// The katalog has ONE field — `manufacturerId` carries both levels — so the
// copied test `children rendered while selectedManufacturerId === row.id` broke
// the control outright: tapping a child moved that single field to the CHILD's
// id, the test went false, and the whole child block unmounted mid-gesture with
// nothing checked and no way to deselect. The `checked={... === child.id}` on
// the child row could never be true — a condition that cannot be true is a
// defect's signature, not a design's.
//
// The family is therefore open while the parent OR ANY OF ITS CHILDREN is the
// selection, and the child row carries the same `=== id ? undefined : id`
// toggle-off every other row in the sheet has. Deselecting a child leaves
// nothing selected and closes the family, which is exactly what deselecting the
// parent does — one behaviour for the section, not two.
//
// THE BRAND SEARCH IS LIVE AND LOCAL, NOT PART OF THE DRAFT. It filters the
// visible OPTION LIST, never the results — catalogFacets.ts's split. Staging it
// would mean typing a brand name, pressing "apply" and seeing nothing change.
//
// >>> THE "SHOW ALL" EXPANDER IS A PROP, NOT LOCAL STATE (review item 3). <<<
// It used to be `useState` here, which the sheet's "Zrusit vsetko" could not
// reach — so a cleared section came back still expanded, while the desktop
// Clear all resets its rail expander (CatalogBrowseV2's `resetBrandFacetUi`).
// It now lives on the draft api, which is the page's, so one verb resets
// everything this section shows and it survives a rotation past 1280.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { SheetCheckRow, SheetSection, SheetTextAction } from "../mobile/sheetAtoms";
import { SearchFieldB } from "../mobile/filterBarAtoms";
import {
  CatalogFacetModel,
  catalogFacetCapBypassed,
  visibleFacetRows,
} from "./catalogFacets";

export interface CatalogSheetBrandsProps {
  facets: CatalogFacetModel;
  brandSearch: string;
  onBrandSearchChange: (value: string) => void;
  /** Total across the current category — the "all brands" row's count. */
  totalCount: number;
  selectedManufacturerId?: string;
  onSelect: (id?: string) => void;
  /** "Show all N brands" — owned by the draft api, see the header. */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

const CatalogSheetBrands: React.FC<CatalogSheetBrandsProps> = ({
  facets,
  brandSearch,
  onBrandSearchChange,
  totalCount,
  selectedManufacturerId,
  onSelect,
  expanded,
  onExpandedChange,
}) => {
  const { translate } = useTranslation("myMachinery");

  const rows = visibleFacetRows(facets, expanded, brandSearch);
  // ONE question, asked once — the rail's own lesson (catalogFacets.ts:130-135):
  // an expander that keeps its own idea of "expanded" while a search silently
  // bypasses the cap becomes a button that changes nothing on screen.
  const capBypassed = catalogFacetCapBypassed(brandSearch);
  const showExpander = !capBypassed && facets.totalRows > rows.length;

  return (
    <SheetSection title={translate("myMachinery:manufacturer")} divider={false}>
      <div style={{ display: "flex", padding: "2px 0 6px" }}>
        <SearchFieldB
          value={brandSearch}
          onChange={onBrandSearchChange}
          // The brand box filters a list; it has no "commit" gesture of its own,
          // so Enter is a no-op rather than a second, different submit.
          onSubmit={() => undefined}
          placeholder={translate("myMachinery:searchBrands")}
          background="var(--bg-soft)"
        />
      </div>

      <SheetCheckRow
        label={translate("myMachinery:allBrands")}
        count={totalCount}
        checked={!selectedManufacturerId}
        onToggle={() => onSelect(undefined)}
      />

      {rows.map((row) => {
        // THE FAMILY, NOT THE PARENT — see the F1 block in this file's header.
        // One draft field carries both levels, so a child selection has to keep
        // its own family on screen or it unmounts the row that is checked.
        const familyOpen =
          selectedManufacturerId === row.id ||
          (!!row.children &&
            row.children.some((child) => child.id === selectedManufacturerId));

        return (
          <React.Fragment key={row.id}>
            <SheetCheckRow
              label={row.name}
              count={row.count}
              checked={selectedManufacturerId === row.id}
              onToggle={() =>
                onSelect(selectedManufacturerId === row.id ? undefined : row.id)
              }
            />
            {row.children &&
              familyOpen &&
              row.children.map((child) => (
                <SheetCheckRow
                  key={child.id}
                  label={child.name}
                  count={child.machineCount}
                  depth={1}
                  checked={selectedManufacturerId === child.id}
                  // The same toggle-off every sibling row has. Deselecting a
                  // child clears the section, exactly as deselecting a parent
                  // does — the family then closes, and a tap on the parent
                  // reopens it.
                  onToggle={() =>
                    onSelect(
                      selectedManufacturerId === child.id ? undefined : child.id
                    )
                  }
                />
              ))}
          </React.Fragment>
        );
      })}

      {showExpander && (
        <SheetTextAction
          label={translate("myMachinery:v2.catalog.showAllBrands", {
            count: facets.totalRows,
          })}
          onClick={() => onExpandedChange(true)}
        />
      )}
      {!capBypassed && expanded && (
        <SheetTextAction
          label={translate("myMachinery:v2.catalog.showFewerBrands")}
          onClick={() => onExpandedChange(false)}
        />
      )}
    </SheetSection>
  );
};

export default CatalogSheetBrands;
